import { apiFetch } from './client';

export interface CustomerSettings {
  /** @deprecated Legacy jsonb feature booleans (pre-2026-08). The real
   *  feature system is feature_flags + customer_feature_overrides — these
   *  were never read by any gate. Kept optional so old rows still parse;
   *  nothing writes them anymore. */
  conferenceEnabled?: boolean;
  /** @deprecated See conferenceEnabled. */
  seriesEnabled?: boolean;
  /** @deprecated See conferenceEnabled. */
  schoolEnabled?: boolean;
  /** @deprecated See conferenceEnabled. */
  shopEnabled?: boolean;
  /** @deprecated See conferenceEnabled. */
  donationsEnabled?: boolean;
  maxChannels?: number;
  maxStorageGb?: number;
  maxRecordings?: number;
  maxIvrNodes?: number;
}

export interface CustomerRow {
  id: string; reseller_id: string; name: string; customer_number: number;
  active: boolean; timezone: string | null; language: 'en' | 'he' | 'yi';
  contact_email: string | null; settings: CustomerSettings;
  /** Default dialing prefix ('+' + 1-3 digits). NULL = inherit the reseller
   *  default, then platform. Migration 0113. */
  default_country_code?: string | null;
  /** Phone-recording mid-call hangup disposition. Migration 0059. */
  phone_recording_hangup_action?: 'auto_save' | 'discard';
  /** Recycle-bin retention days (per-customer). Migration 0059. */
  recording_recycle_bin_retention_days?: number;
  /** Reseller-set max retention; the customer's slider clamps to this.
   *  Returned by GET /api/customers/:id only. Migration 0059. */
  reseller_recycle_bin_retention_days_max?: number;
  /** Phone admin console: tree the dial-in flow auto-enters when admin
   *  picks "manage IVR" and customer has 1 tree. Default in the picker
   *  when multiple. Nullable. Migration 0073. */
  main_tree_id?: string | null;
  /** Phone admin console: per-customer admin_audit_log retention window.
   *  CHECK constraint enforces 30 / 60 / 90. Migration 0073. */
  audit_retention_days?: 30 | 60 | 90;
  created_at: string; updated_at: string;
}

interface CreateBody {
  resellerId?: string;
  name: string;
  contactEmail?: string | null;
  timezone?: string | null;
  language?: 'en' | 'he' | 'yi';
  settings?: CustomerSettings;
  /** Per-customer feature switches from the create form. Keys must exist in
   *  the feature_flags catalog (non-core). The backend writes a
   *  customer_feature_overrides row ONLY where a value differs from the
   *  reseller-effective default. (Replaced the legacy backupBucketEnabled /
   *  authListsEnabled one-off fields, which the backend still accepts.) */
  features?: Record<string, boolean>;
  firstOwner?: {
    email: string; displayName: string | null; phone: string | null;
    method: 'email_invite' | 'manual_password'; manualPassword?: string;
  };
}

export const customers = {
  list: (params: { resellerId?: string; search?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.resellerId) q.set('reseller_id', params.resellerId);
    if (params.search) q.set('search', params.search);
    if (params.limit) q.set('limit', String(params.limit));
    return apiFetch<{ rows: CustomerRow[] }>(`/api/customers?${q}`);
  },
  get: (id: string) => apiFetch<CustomerRow>(`/api/customers/${id}`),
  /** Look up a single customer by its 4-digit customer_number. Used by
   *  the scoped URL layout — no pagination concerns since the backend
   *  query goes straight to the row. Pass resellerId to disambiguate
   *  across resellers (super-admin only path). */
  getByNumber: (customerNumber: number, opts: { resellerId?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.resellerId) q.set('reseller_id', opts.resellerId);
    const qs = q.toString();
    return apiFetch<CustomerRow>(
      `/api/customers/by-number/${customerNumber}${qs ? `?${qs}` : ''}`,
    );
  },
  create: (body: CreateBody) =>
    apiFetch<{ customerId: string; customerNumber: number; firstOwner: { userId: string | null; inviteId: string | null; inviteToken: string | null } | null }>(
      '/api/customers', { method: 'POST', body: JSON.stringify(body) },
    ),
  update: (
    id: string,
    body: Partial<Pick<CustomerRow, 'name' | 'timezone' | 'active' | 'contact_email' | 'settings'>>
        & { language?: 'en' | 'he' | 'yi' }
        & { defaultCountryCode?: string | null }
        & { phoneRecordingHangupAction?: 'auto_save' | 'discard' }
        & { recordingRecycleBinRetentionDays?: number }
        & { mainTreeId?: string | null }
        & { auditRetentionDays?: 30 | 60 | 90 },
  ) =>
    apiFetch<CustomerRow>(`/api/customers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) => apiFetch<void>(`/api/customers/${id}`, { method: 'DELETE' }),
};
